import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  CalendarDays,
  CreditCard,
  DollarSign,
  LogOut,
  MapPin,
  Share2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FINAL_PAYMENT_DATE = 'June 16, 2027';

const balanceDue = (transaction) => {
  if (typeof transaction.balance_due === 'number') return Math.max(transaction.balance_due, 0);
  if (typeof transaction.total_price === 'number') return Math.max(transaction.total_price - (transaction.amount || 0), 0);
  return 0;
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    condoBookings: 0,
    villaBookings: 0,
    totalGuests: 0,
    pendingPayments: 0,
    completedPayments: 0,
    totalBalanceDue: 0,
    referralVisits: 0,
    referralSignups: 0,
  });

  const calculateStats = useCallback((bookingData, referralData) => {
    const bookingRecords = bookingData.filter((transaction) => transaction.payment_type !== 'balance_payment');
    const completed = bookingRecords.filter((transaction) => transaction.payment_status === 'paid');
    const allPaidPayments = bookingData.filter((transaction) => transaction.payment_status === 'paid');
    const pending = bookingRecords.filter((transaction) => transaction.payment_status === 'pending');

    setStats({
      totalRevenue: allPaidPayments.reduce((sum, transaction) => sum + (transaction.amount || 0), 0),
      totalBookings: completed.length,
      condoBookings: completed.filter((transaction) => transaction.accommodation_type === 'condo').length,
      villaBookings: completed.filter((transaction) => transaction.accommodation_type === 'villa').length,
      totalGuests: completed.reduce((sum, transaction) => sum + (transaction.guest_count || 0), 0),
      pendingPayments: pending.length,
      completedPayments: completed.length,
      totalBalanceDue: completed.reduce((sum, transaction) => sum + balanceDue(transaction), 0),
      referralVisits: referralData.reduce((sum, referral) => sum + (referral.visits || 0), 0),
      referralSignups: referralData.reduce((sum, referral) => sum + (referral.signups || 0), 0),
    });
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      setError('');
      const [transactionsResponse, referralsResponse] = await Promise.all([
        axios.get(`${API}/payments/transactions`, { withCredentials: true }),
        axios.get(`${API}/referrals/admin`, { withCredentials: true }),
      ]);
      const bookingData = transactionsResponse.data;
      const referralData = referralsResponse.data;
      setTransactions(bookingData);
      setReferrals(referralData);
      calculateStats(bookingData, referralData);
    } catch (requestError) {
      console.error('Error fetching admin dashboard:', requestError);
      setError('Unable to load the dashboard. Please sign in again or try later.');
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const referralConversion = stats.referralVisits > 0
    ? (stats.referralSignups / stats.referralVisits) * 100
    : 0;

  return (
    <main className="admin-dashboard-page" data-testid="admin-dashboard">
      <div className="admin-dashboard-shell">
        <header className="admin-dashboard-header">
          <div>
            <p className="eyebrow">Owner command center</p>
            <h1>Siege's Playcation Dashboard</h1>
            <p>Bookings, balances, and referrals for July 16-18, 2027</p>
          </div>
          <div className="admin-user-card">
            <span>{user?.email}</span>
            <Button variant="outline" className="outline-gold" data-testid="admin-logout-button" onClick={logout}>
              <LogOut className="h-4 w-4" />
              Log out
            </Button>
          </div>
        </header>

        {error && <div className="admin-error" data-testid="admin-dashboard-error">{error}</div>}

        <section className="admin-stat-grid" aria-label="Booking statistics">
          <AdminStat icon={Users} label="Total bookings" value={stats.totalBookings} note={`${stats.pendingPayments} pending`} />
          <AdminStat icon={TrendingUp} label="Confirmed guests" value={stats.totalGuests} note="Paid attendees" />
          <AdminStat icon={MapPin} label="Condos / Villas" value={`${stats.condoBookings} / ${stats.villaBookings}`} note="Paid bookings" />
          <AdminStat icon={DollarSign} label="Balance due" value={`$${stats.totalBalanceDue.toLocaleString()}`} note={`Due ${FINAL_PAYMENT_DATE}`} testId="admin-balance-due-card" />
        </section>

        <div className="admin-two-column">
          <Card className="admin-panel" data-testid="balance-tracker">
            <CardHeader>
              <CardTitle className="admin-panel-title"><CalendarDays /> Final Payment Tracker</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? <p className="admin-empty">Loading balances...</p> : (
                <div className="admin-list">
                  {transactions.filter((transaction) => transaction.payment_status === 'paid' && transaction.payment_type !== 'balance_payment').map((transaction) => (
                    <div className="admin-list-row" key={`balance-${transaction.session_id}`}>
                      <div>
                        <strong>{transaction.customer_info?.firstName} {transaction.customer_info?.lastName}</strong>
                        <span>{transaction.customer_info?.email}</span>
                      </div>
                      <div>
                        <span>Balance due</span>
                        <strong>${balanceDue(transaction).toLocaleString()}</strong>
                      </div>
                    </div>
                  ))}
                  {!loading && stats.completedPayments === 0 && <p className="admin-empty">No paid bookings yet.</p>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="admin-panel" data-testid="referral-summary-card">
            <CardHeader>
              <CardTitle className="admin-panel-title"><Share2 /> Referral Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="referral-metrics">
                <div><span>Visits</span><strong>{stats.referralVisits}</strong></div>
                <div><span>Signups</span><strong>{stats.referralSignups}</strong></div>
                <div><span>Conversion</span><strong>{referralConversion.toFixed(1)}%</strong></div>
              </div>
              <div className="admin-list">
                {referrals.slice(0, 5).map((referral) => (
                  <div className="admin-list-row" key={referral.code} data-testid={`referral-row-${referral.code}`}>
                    <div>
                      <strong>{referral.owner_name || 'Guest'}</strong>
                      <span>{referral.code}</span>
                    </div>
                    <div>
                      <span>{referral.visits || 0} visits</span>
                      <strong>{referral.signups || 0} signups</strong>
                    </div>
                  </div>
                ))}
                {!loading && referrals.length === 0 && <p className="admin-empty">Referral links appear after guests book.</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="admin-panel admin-bookings-card">
          <CardHeader>
            <CardTitle className="admin-panel-title"><CreditCard /> Recent Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <p className="admin-empty">Loading bookings...</p> : (
              <div className="admin-booking-list">
                {transactions.map((transaction) => (
                  <article className="admin-booking-row" key={transaction.session_id} data-testid="admin-booking-row">
                    <div>
                      <strong>{transaction.customer_info?.firstName} {transaction.customer_info?.lastName}</strong>
                      <span>{transaction.customer_info?.email}</span>
                      <small>Booked {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'recently'}</small>
                    </div>
                    <div>
                      <span>Guests</span>
                      <strong>{transaction.guest_count || 0}</strong>
                    </div>
                    <div>
                      <span>Stay</span>
                      <strong>{transaction.accommodation_type || 'pending'}</strong>
                    </div>
                    <div>
                      <span>Paid</span>
                      <strong>${(transaction.amount || 0).toLocaleString()}</strong>
                    </div>
                    <div>
                      <span>Referral</span>
                      <strong>{transaction.referral_code || 'Not created'}</strong>
                      {transaction.referred_by && <small>Referred by {transaction.referred_by}</small>}
                    </div>
                    <Badge className={transaction.payment_status === 'paid' ? 'admin-paid-badge' : 'admin-pending-badge'}>
                      {transaction.payment_status}
                    </Badge>
                  </article>
                ))}
                {transactions.length === 0 && <p className="admin-empty">No bookings yet.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

const AdminStat = ({ icon: Icon, label, value, note, testId }) => (
  <Card className="admin-stat-card" data-testid={testId}>
    <CardContent className="p-6">
      <div className="admin-stat-icon"><Icon className="h-5 w-5" /></div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </CardContent>
  </Card>
);

export default AdminDashboard;
